import { forwardRef, MouseEventHandler, useEffect } from "react";
import DatePicker, { DatePickerProps } from "react-datepicker";
import { FiCalendar } from "react-icons/fi";

import { Box, Input, InputGroup, useToken } from "@chakra-ui/react";

import { scopedTestIds, type TestIdProps } from "../utils/test-ids";

type ResponsiveValue<T> = T | Partial<Record<string, T>> | Array<T | null>;
type SystemStyleObject = Record<string, unknown>;

import type { Locale } from "date-fns";

import "react-datepicker/dist/react-datepicker.css";

type CalendarProps = DatePickerProps &
  TestIdProps & {
    size?: ResponsiveValue<(string & unknown) | "sm" | "md" | "lg" | "xs">;
    ariaLabel?: string;
    isInsideIframe?: boolean;
    fontSize?: ResponsiveValue<string & unknown>;
    calendarInputDataTestId?: string;
  };

function Calendar(props: CalendarProps) {
  const {
    size,
    locale = "en-AU",
    isInsideIframe,
    fontSize,
    calendarInputDataTestId,
    "data-testid": rootTestId,
    ...datePickerProps
  } = props;

  // Explicit input id wins; otherwise derive `-input` from the root id.
  const t = scopedTestIds(rootTestId);
  const inputDataTestId = calendarInputDataTestId ?? t.child("input");
  const [bodyFont] = useToken("fonts", ["body"]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const activeElement = document.activeElement as HTMLElement;
      if (!activeElement) return;

      const datepickerWrapper = activeElement.closest(
        ".react-datepicker-wrapper"
      );
      if (!datepickerWrapper) return;

      const datepickerInput = datepickerWrapper.querySelector(
        "input"
      ) as HTMLInputElement;
      if (!datepickerInput) return;

      const calendarElement = document.querySelector(
        `.react-datepicker[data-testid="${
          datepickerInput.getAttribute("data-testid") || "default"
        }"], .react-datepicker`
      );

      if (!calendarElement) {
        const allCalendars = document.querySelectorAll(".react-datepicker");
        const allWrappers = document.querySelectorAll(
          ".react-datepicker-wrapper"
        );
        const wrapperIndex = Array.from(allWrappers).indexOf(datepickerWrapper);
        const targetCalendar = allCalendars[wrapperIndex];
        if (!targetCalendar) return;
      }

      const targetCalendar =
        calendarElement || document.querySelector(".react-datepicker");
      if (!targetCalendar) return;

      if (activeElement === datepickerInput) {
        event.preventDefault();
        event.stopPropagation();

        setTimeout(() => {
          const dayToFocus =
            targetCalendar.querySelector(".react-datepicker__day--selected") ||
            targetCalendar.querySelector(
              ".react-datepicker__day:not(.react-datepicker__day--disabled):not(.react-datepicker__day--outside-month)"
            );
          (dayToFocus as HTMLElement)?.focus();
        }, 0);
        return;
      }

      if (targetCalendar.contains(activeElement)) {
        return;
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, []);

  const CalendarStyles: SystemStyleObject = {
    "& .react-datepicker, & .react-datepicker *": {
      fontFamily: bodyFont,
      outlineColor: "brand.500"
    },

    "& .react-datepicker-wrapper, & .react-datepicker-wrapper *": {
      fontFamily: bodyFont
    },

    "& .react-datepicker-wrapper": {
      width: "100%"
    },

    "& .react-datepicker": {
      border: "none",
      boxShadow: "lg",

      "&:focus-within": {
        outline: "2px solid",
        outlineColor: "brand.500",
        outlineOffset: "2px"
      }
    },

    "& .react-datepicker__header": {
      bg: "gray.100",
      borderBottom: "none"
    },

    "& .react-datepicker__current-month": {
      mt: 1.5,
      fontSize: "md",
      color: "gray.800"
    },

    "& .react-datepicker__header__dropdown": {
      mt: 2.5,
      display: "flex",
      justifyContent: "space-between"
    },

    "& .react-datepicker__navigation": {
      top: 3.5
    },

    "& .react-datepicker__navigation-icon--next::before": {
      borderColor: "gray.800",

      "&:hover": {
        borderColor: "gray.900"
      }
    },

    "& .react-datepicker__navigation-icon--previous::before": {
      borderColor: "gray.800",

      "&:hover": {
        borderColor: "gray.900"
      }
    },

    "& .react-datepicker__header__dropdown select": {
      bg: "gray.100",
      fontSize: "sm",
      fontWeight: 500,
      color: "gray.800"
    },

    "& .react-datepicker__close-icon": {
      right: size === "sm" ? 10 : size === "md" ? "45px" : "57px",
      top: size === "sm" ? 2 : size === "md" ? 2.5 : 3,
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      padding: 0,
      display: "inline-block"
    },

    "& .react-datepicker__close-icon::after": {
      bg: "transparent",
      fontSize: size === "sm" ? "2xl" : size === "md" ? "3xl" : "40px",
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      padding: 0,
      lineHeight: 0,
      color: "currentColor"
    },
    "& [data-part='end-element']": {
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      top: size === "sm" ? 2 : size === "md" ? 2.5 : 3.5,
      right: size === "sm" ? 2 : size === "md" ? "9px" : "9px"
    },
    "& .react-datepicker__day-name, & .react-datepicker__day, & .react-datepicker__time-name":
      {
        w: 8,
        lineHeight: "2rem",
        color: "gray.800",

        "&:hover": {
          bg: "gray.100"
        },

        "&:focus": {
          outline: "2px solid",
          outlineColor: "brand.500",
          outlineOffset: "2px"
        }
      },

    "& .react-datepicker__day--outside-month": {
      color: "gray.500"
    },

    "& .react-datepicker__day--keyboard-selected, & .react-datepicker__month-text--keyboard-selected, & .react-datepicker__quarter-text--keyboard-selected, & .react-datepicker__year-text--keyboard-selected":
      {
        bg: "brand.50",

        "&:hover": {
          bg: "brand.200"
        },

        "&:focus": {
          outline: "2px solid",
          outlineColor: "brand.500",
          outlineOffset: "2px",
          bg: "brand.50"
        }
      },

    "& .react-datepicker__day--selected, & .react-datepicker__day--in-selecting-range, & .react-datepicker__day--in-range, & .react-datepicker__month-text--selected, & .react-datepicker__month-text--in-selecting-range, & .react-datepicker__month-text--in-range, & .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul .react-datepicker__time-list li .react-datepicker__time-list-item--selected":
      {
        bg: "brand.500",
        color: "white",

        "&:hover": {
          bg: "brand.600"
        },

        "&:focus": {
          outline: "2px solid",
          outlineColor: "white",
          outlineOffset: "2px",
          bg: "brand.500"
        }
      },
    "& svg": {
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      color: "currentColor"
    },
    "& .react-datepicker__portal": {
      background: "blackAlpha.600"
    }
  };

  const getLocale = (): Locale => {
    return require(`date-fns/locale/${locale}/index.js`);
  };

  const ChakraUIInput = forwardRef<
    HTMLInputElement,
    { onClick?: MouseEventHandler<HTMLDivElement> }
  >((inputProps, ref) => (
    <InputGroup
      endElement={<FiCalendar />}
      endElementProps={{ onClick: inputProps.onClick }}
    >
      <Input
        {...inputProps}
        type="text"
        ref={ref}
        size={
          size as "sm" | "md" | "lg" | "xl" | "2xl" | "2xs" | "xs" | undefined
        }
        fontSize={fontSize}
        data-testid={inputDataTestId}
      />
    </InputGroup>
  ));

  return (
    <Box css={CalendarStyles}>
      <DatePicker
        {...datePickerProps}
        isClearable
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        showPopperArrow={false}
        customInput={<ChakraUIInput aria-label={datePickerProps.ariaLabel} />}
        locale={getLocale()}
        withPortal={isInsideIframe}
      />
    </Box>
  );
}

export { Calendar };
export type { CalendarProps };
